import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useApiGet, useApiSend } from '../../utils/httpClient';
import {
  getUserCommittees,
  transferPost,
  initiateMember,
} from '../../endpoints/usersTs';
import { getCalendarYearStart } from '../../utils/variables';
import dayjs from 'dayjs';
import ChapterSelector from '../../Components/ChapterSelector';
import DateFilter from '../../Components/DateFilter';
import { Cancel, Save } from '@mui/icons-material';

export default function UserMemberInfo({
  id,
  canAction,
  refetchParent,
  needToRefetch,
  updateNeedToRefetch,
}) {
  const [showMoreChapters, setShowMoreChapters] = useState(false);
  const [showMoreCommittees, setShowMoreCommittees] = useState(false);
  const [showMoreOfficers, setShowMoreOfficers] = useState(false);
  const [showMoreTLC, setShowMoreTLC] = useState(false);
  const [showMoreBnB, setShowMoreBnB] = useState(false);
  const [showMoreReciprocity, setShowMoreReciprocity] = useState(false);

  const [localChapterId, setLocalChapterId] = useState(null);
  const [localChapterSelect, setLocalChapterSelect] = useState(false);
  const [transferDate, setTransferDate] = useState(null);
  const [errorSaving, setErrorSaving] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [localChapterInitiated, setLocalChapterInitiated] = useState(false);

  const {
    data: userInfo,
    isLoading,
    refetch,
  } = useApiGet(
    ['userCommittees', id],
    getUserCommittees,
    {
      enabled: !!id,
    },
    { id }
  );

  const handleCancel = () => {
    setLocalChapterSelect(false);
    setLocalChapterId(null);
    setTransferDate(null);
    setErrorSaving(null);
    setIsSaving(false);
    setLocalChapterInitiated(false);
  };

  const handleCancelInitiate = () => {
    setLocalChapterId(null);
    setTransferDate(dayjs());
    setErrorSaving(null);
    setIsSaving(false);
    setLocalChapterInitiated(false);
  };

  const handleMakeRefetch = () => {
    if (needToRefetch) {
      return;
    } else {
      updateNeedToRefetch(true);
    }
  };

  const { mutate: transferMember } = useApiSend(
    transferPost,
    (data) => {
      handleCancel();
      refetch();
      refetchParent();
      handleMakeRefetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: initiateMemberTransfer } = useApiSend(
    initiateMember,
    (data) => {
      handleCancelInitiate();
      refetchParent();
      refetch();
      handleMakeRefetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const transferWords = () => {
    let transfer = false;
    if (userInfo?.chapter_history?.length > 0) {
      userInfo.chapter_history.forEach((item) => {
        if (item.end_date === null) {
          transfer = true;
        } else if (
          dayjs(item.end_date).isAfter(dayjs(getCalendarYearStart()))
        ) {
          transfer = true;
        }
      });
    }
    return transfer ? 'Transfer' : 'Reinstate';
  };

  const handleInitiate = () => {
    setLocalChapterInitiated(true);
    setLocalChapterId(null);
    setTransferDate(dayjs());
    setErrorSaving(null);
    setIsSaving(false);
  };

  const handleSaveInitiate = () => {
    if (!localChapterId || !transferDate) {
      setErrorSaving('Please select a chapter and transfer date');
      return;
    }
    setIsSaving(true);
    initiateMemberTransfer({
      user_id: id,
      local_chapter_id: localChapterId,
      transfer_date: transferDate.format('YYYY-MM-DD'),
    });
  };

  const handleSaveTransfer = () => {
    if (!localChapterId || !transferDate) {
      setErrorSaving('Please select a chapter and transfer date');
      return;
    }
    setIsSaving(true);
    transferMember({
      user_id: id,
      local_chapter_id: localChapterId,
      transfer_date: transferDate.format('YYYY-MM-DD'),
    });
  };
  const handleReinstate = () => {
    setLocalChapterSelect(true);
    setLocalChapterId(null);
    setTransferDate(dayjs());
    setErrorSaving(null);
    setIsSaving(false);
  };

  const getPositionName = (item) => {
    let positionName = item.position;
    if (item.local_chapter_name) {
      positionName = `${item.position} (${item.local_chapter_name})`;
    }
    if (item.spd_chapter_name) {
      positionName = `${item.position} (${item.spd_chapter_name})`;
    }
    return positionName;
  };

  if (isLoading) {
    return (
      <Grid container>
        <Grid item xs={12} padding>
          <Typography variant="h6">Loading...</Typography>
        </Grid>
      </Grid>
    );
  }
  if (!userInfo) {
    return (
      <Grid container>
        <Grid item xs={12} padding>
          <Typography variant="h6">User not found</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      {localChapterInitiated && (
        <Grid container item xs={12}>
          <Grid item xs={12} md={6} padding>
            <ChapterSelector
              chapter={localChapterId}
              handleChapterSelect={setLocalChapterId}
              spd={null}
              reportId={'userMemberInfo'}
              label={'Select Chapter'}
            />
          </Grid>
          <Grid item xs={12} md={6} padding>
            <DateFilter
              value={transferDate}
              label={'Initiation Date'}
              updateFilters={setTransferDate}
            />
          </Grid>
          {errorSaving && (
            <Grid item xs={12} padding>
              <Typography variant="body2" color="error">
                {errorSaving}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} padding textAlign={'right'}>
            <Button
              variant="contained"
              size="small"
              onClick={handleCancelInitiate}
              disabled={!canAction}
              color={'warning'}
              sx={{ fontWeight: 'bold' }}
            >
              <Cancel sx={{ fontSize: 16, pr: 1 }} />
              {'Cancel'}
            </Button>
            <LoadingButton
              variant="contained"
              size="small"
              onClick={handleSaveInitiate}
              color={'green'}
              sx={{ fontWeight: 'bold', ml: 1 }}
              loading={isSaving}
              disabled={isSaving}
            >
              <Save sx={{ fontSize: 16, pr: 1 }} />
              Initiate
            </LoadingButton>
          </Grid>
        </Grid>
      )}
      {userInfo?.chapter_history?.length === 0 &&
        !!!localChapterInitiated &&
        canAction && (
          <Grid item xs={12} padding textAlign={'right'}>
            <Button
              variant="contained"
              size="small"
              onClick={handleInitiate}
              sx={{ fontWeight: 'bold' }}
            >
              {'Initiate'}
            </Button>
          </Grid>
        )}
      {userInfo?.chapter_history?.length > 0 && (
        <Grid item xs={12} md={6} padding>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <Grid container justifyContent={'space-between'}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6" component={'span'}>
                      Chapters
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setShowMoreChapters(!showMoreChapters)}
                      sx={{ ml: 2 }}
                    >
                      {showMoreChapters ? 'Show Less' : 'Show More'}
                    </Button>
                  </Grid>
                  {canAction && (
                    <Grid item xs={12} sm={6} textAlign={'right'}>
                      {localChapterSelect && (
                        <Button
                          variant={'contained'}
                          size="small"
                          color={'warning'}
                          sx={{ mr: 1, fontWeight: 'bold' }}
                          onClick={handleCancel}
                          disabled={isSaving}
                        >
                          <Cancel sx={{ fontSize: 16, pr: 1 }} />
                          Cancel
                        </Button>
                      )}
                      <LoadingButton
                        variant="contained"
                        size="small"
                        onClick={
                          localChapterSelect
                            ? handleSaveTransfer
                            : handleReinstate
                        }
                        color={localChapterSelect ? 'green' : 'primary'}
                        sx={{ fontWeight: 'bold' }}
                        loading={isSaving}
                        disabled={isSaving}
                      >
                        {localChapterSelect && (
                          <Save sx={{ fontSize: 16, pr: 1 }} />
                        )}
                        {localChapterSelect ? 'Save' : transferWords()}
                      </LoadingButton>
                    </Grid>
                  )}
                </Grid>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {showMoreChapters && (
                <Grid container>
                  {userInfo?.chapter_history?.map((item, index) => (
                    <Grid item container xs={12} key={item.id} padding>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component={'span'}
                          fontWeight={'bold'}
                        >
                          {item.name}
                        </Typography>
                        {item.initiated && (
                          <Typography
                            variant="caption"
                            component={'span'}
                            color={'primary'}
                            sx={{ ml: 2, fontWeight: 'bold' }}
                          >
                            Initiated
                          </Typography>
                        )}
                        {item.reinstated && (
                          <Typography
                            variant="caption"
                            component={'span'}
                            color={'primary'}
                            sx={{ ml: 2, fontWeight: 'bold' }}
                          >
                            Reinstated
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component={'span'}>
                          {dayjs(item.start_date).format('MM/DD/YYYY')} -{' '}
                          {item.end_date
                            ? dayjs(item.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
              {localChapterSelect && (
                <Grid container>
                  <Grid item xs={12} padding>
                    <ChapterSelector
                      chapter={localChapterId}
                      handleChapterSelect={setLocalChapterId}
                      spd={null}
                      reportId={'userMemberInfo'}
                      label={'Select Chapter'}
                    />
                  </Grid>
                  <Grid item xs={12} padding>
                    <DateFilter
                      value={transferDate}
                      label={`${transferWords()} Date`}
                      updateFilters={setTransferDate}
                    />
                  </Grid>
                  {errorSaving && (
                    <Grid item xs={12} padding>
                      <Typography variant="body2" color="error">
                        {errorSaving}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {userInfo?.officers?.length > 0 && (
        <Grid item xs={12} md={6} padding>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <React.Fragment>
                  <Typography variant="h6" component={'span'}>
                    Chapter Leadership
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowMoreOfficers(!showMoreOfficers)}
                    sx={{ ml: 2 }}
                  >
                    {showMoreOfficers ? 'Show Less' : 'Show More'}
                  </Button>
                </React.Fragment>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {showMoreOfficers && (
                <Grid container>
                  {userInfo?.officers?.map((item, index) => (
                    <Grid item container xs={12} key={item.id} padding>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component={'span'}
                          fontWeight={'bold'}
                        >
                          {item.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" component={'span'}>
                          {getPositionName(item)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component={'span'}>
                          {item.start_date
                            ? dayjs(item.start_date).format('MM/DD/YYYY')
                            : ''}{' '}
                          -{' '}
                          {item.end_date
                            ? dayjs(item.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {userInfo?.committees?.length > 0 && (
        <Grid item xs={12} md={6} padding>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <React.Fragment>
                  <Typography variant="h6" component={'span'}>
                    Committees
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowMoreCommittees(!showMoreCommittees)}
                    sx={{ ml: 2 }}
                  >
                    {showMoreCommittees ? 'Show Less' : 'Show More'}
                  </Button>
                </React.Fragment>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {showMoreCommittees && (
                <Grid container>
                  {userInfo?.committees?.map((item, index) => (
                    <Grid item container xs={12} key={item.id} padding>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component={'span'}
                          fontWeight={'bold'}
                        >
                          {item.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" component={'span'}>
                          {getPositionName(item)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component={'span'}>
                          {item.start_date
                            ? dayjs(item.start_date).format('MM/DD/YYYY')
                            : ''}{' '}
                          -{' '}
                          {item.end_date
                            ? dayjs(item.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {userInfo?.tlc?.length > 0 && (
        <Grid item xs={12} md={6} padding>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <React.Fragment>
                  <Typography variant="h6" component={'span'}>
                    TLCs
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowMoreTLC(!showMoreTLC)}
                    sx={{ ml: 2 }}
                  >
                    {showMoreTLC ? 'Show Less' : 'Show More'}
                  </Button>
                </React.Fragment>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {showMoreTLC && (
                <Grid container>
                  {userInfo?.tlc?.map((item, index) => (
                    <Grid item container xs={12} key={item.id} padding>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component={'span'}
                          fontWeight={'bold'}
                        >
                          {item.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" component={'span'}>
                          {getPositionName(item)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component={'span'}>
                          {item.start_date
                            ? dayjs(item.start_date).format('MM/DD/YYYY')
                            : ''}{' '}
                          -{' '}
                          {item.end_date
                            ? dayjs(item.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}
      {userInfo?.bnb?.length > 0 && (
        <Grid item xs={12} md={6} padding>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <React.Fragment>
                  <Typography variant="h6" component={'span'}>
                    Bed and Breakfast
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowMoreBnB(!showMoreBnB)}
                    sx={{ ml: 2 }}
                  >
                    {showMoreBnB ? 'Show Less' : 'Show More'}
                  </Button>
                </React.Fragment>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {showMoreBnB && (
                <Grid container>
                  {userInfo?.bnb?.map((item, index) => (
                    <Grid item container xs={12} key={item.id} padding>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component={'span'}
                          fontWeight={'bold'}
                        >
                          {item.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" component={'span'}>
                          {getPositionName(item)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component={'span'}>
                          {item.start_date
                            ? dayjs(item.start_date).format('MM/DD/YYYY')
                            : ''}{' '}
                          -{' '}
                          {item.end_date
                            ? dayjs(item.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {userInfo?.reciprocity?.length > 0 && (
        <Grid item xs={12} md={6} padding>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={
                <React.Fragment>
                  <Typography variant="h6" component={'span'}>
                    Reciprocity
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowMoreReciprocity(!showMoreReciprocity)}
                    sx={{ ml: 2 }}
                  >
                    {showMoreReciprocity ? 'Show Less' : 'Show More'}
                  </Button>
                </React.Fragment>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {showMoreReciprocity && (
                <Grid container>
                  {userInfo?.reciprocity?.map((item, index) => (
                    <Grid item container xs={12} key={item.id} padding>
                      <Grid item xs={12}>
                        <Typography
                          variant="body1"
                          component={'span'}
                          fontWeight={'bold'}
                        >
                          {item.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1" component={'span'}>
                          {getPositionName(item)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" component={'span'}>
                          {item.start_date
                            ? dayjs(item.start_date).format('MM/DD/YYYY')
                            : ''}{' '}
                          -{' '}
                          {item.end_date
                            ? dayjs(item.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}
      {userInfo?.tlc?.length === 0 &&
        userInfo?.bnb?.length === 0 &&
        userInfo?.reciprocity?.length === 0 &&
        userInfo?.committees?.length === 0 &&
        userInfo?.officers?.length === 0 &&
        userInfo?.chapter_history?.length === 0 && (
          <Grid item xs={12} padding textAlign="center">
            <Typography variant="body1">No membership information</Typography>
          </Grid>
        )}
    </Grid>
  );
}
